import React, { useMemo, useState } from 'react';
import { Button, Empty, Input } from 'antd';
import { FctEstado, FctIcono } from '../../fct/react';
import { fmtFecha } from '../../fct/react/formato';
import type { PropuestaAlta } from '../../api/transmisionesAltaApi';
import { estadoAlta } from './estadosAlta';

interface ListaPropuestasProps {
  propuestas: PropuestaAlta[];
  onAbrir: (propuesta: PropuestaAlta) => void;
  onNueva: () => void;
}

// El texto con que se busca una propuesta: su farmacia, su provincia y sus titulares, sin tildes ni mayúsculas.
const normalizar = (texto: string) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const textoDe = (p: PropuestaAlta) =>
  normalizar([p.farmacia, p.provincia, ...p.titulares.map((t) => t.nombre), ...p.nuevosTitulares.map((t) => t.nombre)].filter(Boolean).join(' '));

/** El primer titular y cuántos más hay: «Ana Pérez (+2 más)». */
export const resumenTitulares = (p: PropuestaAlta) => {
  if (p.titulares.length === 0) return 'Sin titulares';
  const primero = p.titulares[0].nombre ?? `Cliente ${p.titulares[0].id}`;
  return p.titulares.length > 1 ? `${primero} (+${p.titulares.length - 1} más)` : primero;
};

/**
 * Las propuestas del asesor, una tarjeta por propuesta, que se abre con un toque. El buscador filtra por farmacia, provincia
 * y titulares. «Nueva propuesta» es el único botón primario de la pantalla.
 */
const ListaPropuestas: React.FC<ListaPropuestasProps> = ({ propuestas, onAbrir, onNueva }) => {
  const [texto, setTexto] = useState('');
  const visibles = useMemo(() => {
    const buscado = normalizar(texto.trim());
    return buscado ? propuestas.filter((p) => textoDe(p).includes(buscado)) : propuestas;
  }, [propuestas, texto]);

  return (
    <div className="gc-alta-lista">
      <Button type="primary" size="large" block icon={<FctIcono nombre="mas" />} onClick={onNueva}>
        Nueva propuesta
      </Button>
      <Input.Search
        allowClear
        size="large"
        placeholder="Buscar por farmacia o titular"
        aria-label="Buscar propuestas"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
      />
      {visibles.length === 0 ? (
        <Empty description={propuestas.length === 0 ? 'Todavía no has registrado ninguna propuesta' : 'Ninguna propuesta coincide con la búsqueda'} />
      ) : (
        <ul className="gc-alta-lista__items" aria-label="Tus propuestas">
          {visibles.map((p) => {
            const estado = estadoAlta(p.estadoId);
            return (
              <li key={p.id}>
                <button type="button" className="gc-alta-item" onClick={() => onAbrir(p)}>
                  <span className="gc-alta-item__farmacia">{p.farmacia ?? `Farmacia ${p.farmaciaId}`}</span>
                  <span className="gc-alta-item__titulares">{resumenTitulares(p)}</span>
                  <span className="gc-alta-item__pie">
                    <FctEstado personalizado={estado} />
                    <span className="fct-mono">{fmtFecha(p.estadoId === 0 && p.enVentaDesde ? p.enVentaDesde : p.fecha)}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ListaPropuestas;
